import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService, type RoleFilters } from '../userService';

export function useUsers(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['users', page, limit, search],
    queryFn: () => userService.getUsers(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => userService.getUser(id),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: userService.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => userService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUserRoles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, roleIds }: { id: string; roleIds: string[] }) => userService.updateUserRoles(id, roleIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useRoles(page = 1, limit = 100, filters: RoleFilters = {}) {
  return useQuery({
    queryKey: ['roles', page, limit, filters.search ?? '', filters.status ?? '', filters.websiteOnly ?? null],
    queryFn: () => userService.getRoles(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 60000,
  });
}

export function useRole(id: string) {
  return useQuery({
    queryKey: ['roles', id],
    queryFn: () => userService.getRole(id),
    enabled: !!id,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: userService.createRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => userService.updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.deleteRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

export function useRolePermissions(id: string) {
  return useQuery({
    queryKey: ['roles', id, 'permissions'],
    queryFn: () => userService.getRolePermissions(id),
    enabled: !!id,
  });
}

export function useUpdateRolePermissions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permissionIds }: { id: string; permissionIds: string[] }) =>
      userService.updateRolePermissions(id, permissionIds),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['roles', vars.id, 'permissions'] });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

export function useRoleUsers(id: string) {
  return useQuery({
    queryKey: ['roles', id, 'users'],
    queryFn: () => userService.getRoleUsers(id),
    enabled: !!id,
  });
}

export function usePermissionsGrouped(enabled = true) {
  return useQuery({
    queryKey: ['permissions', 'grouped'],
    queryFn: () => userService.getPermissionsGrouped(),
    staleTime: Infinity,
    enabled,
  });
}

export function useCheckUsernameAvailability(username: string, excludeId?: string) {
  return useQuery({
    queryKey: ['users', 'check-username', username, excludeId ?? ''],
    queryFn: () => userService.checkUsernameAvailability(username, excludeId),
    enabled: username.trim().length >= 3,
    staleTime: 10000,
    retry: false,
  });
}

export function useAdminResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
      userService.adminResetPassword(id, newPassword),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useSendResetLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.sendResetLink(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * Generates a password reset link and returns it without sending an email, so
 * an admin can copy/share it directly.
 */
export function useGenerateResetLink() {
  return useMutation({
    mutationFn: (id: string) => userService.generateResetLink(id),
  });
}
